import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../hooks/useSession";
import { Icon } from "./icons";

/**
 * Top-bar account menu.
 *
 * Before this existed the profile control *was* the logout button: a single
 * click on your own name signed you out, with a chevron promising a menu that
 * never opened. That is both a discoverability failure (there was no findable
 * "Log Out") and a footgun. This replaces it with a real menu:
 *
 *   - opens on click, closes on outside click, Escape, or choosing an item;
 *   - exposes an explicit, labelled **Log out** item;
 *   - links to account settings and billing;
 *   - keyboard reachable and screen-reader labelled (`aria-haspopup`,
 *     `aria-expanded`, `role="menu"`/`menuitem`).
 *
 * Logging out ends the ODIN session only. Linked Google/Discord identities stay
 * linked — revoking a provider is a separate, deliberate action under account
 * settings.
 */
export function AccountMenu(): JSX.Element | null {
  const { state, logout } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (state.kind !== "authenticated") {
    return (
      <Link to="/login" className="ax-btn ax-btn--secondary">
        Sign in
      </Link>
    );
  }

  const { me } = state;
  const name = me.displayName ?? me.username;
  const initial = name ? name.charAt(0).toUpperCase() : "?";

  const signOut = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    // `logout` resolves only once the local session is anonymous, so by the time
    // we navigate there is nothing authenticated left to render.
    await logout();
    setOpen(false);
    setBusy(false);
    navigate("/login", { replace: true });
  };

  return (
    <div ref={rootRef} style={rootStyle}>
      <button
        ref={triggerRef}
        type="button"
        className="ax-profile"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${name}`}
      >
        <span className="ax-avatar" aria-hidden="true">
          {initial}
        </span>
        <span className="ax-profile__name">{name}</span>
        <Icon.chevronDown style={{ width: 14, height: 14, flex: "none" }} />
      </button>

      {open ? (
        <div role="menu" aria-label="Account" style={menuStyle}>
          <div style={headStyle}>
            <p style={headNameStyle}>{name}</p>
            <p style={headMetaStyle}>Signed in to ODIN</p>
          </div>

          <div style={dividerStyle} />

          <Link
            to="/app/settings"
            role="menuitem"
            style={itemStyle}
            onClick={() => setOpen(false)}
          >
            Account settings
          </Link>
          <Link to="/app/billing" role="menuitem" style={itemStyle} onClick={() => setOpen(false)}>
            Billing &amp; plan
          </Link>

          <div style={dividerStyle} />

          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut()}
            disabled={busy}
            style={logoutStyle}
          >
            {busy ? "Logging out…" : "Log out"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

const rootStyle: CSSProperties = { position: "relative" };

const menuStyle: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  minWidth: 232,
  background: "var(--surface-raised)",
  border: "1px solid var(--border-default)",
  borderRadius: 12,
  padding: 10,
  zIndex: 60,
  boxShadow: "0 12px 34px rgba(0, 0, 0, 0.45)",
};

const headStyle: CSSProperties = { padding: "2px 6px 6px" };

const headNameStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--text-sm)",
  fontWeight: 600,
  color: "var(--text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const headMetaStyle: CSSProperties = {
  margin: "2px 0 0",
  fontSize: "var(--text-xs)",
  color: "var(--text-tertiary)",
};

const dividerStyle: CSSProperties = {
  height: 1,
  background: "var(--border-subtle)",
  margin: "8px 0",
};

const itemStyle: CSSProperties = {
  display: "block",
  width: "100%",
  padding: "8px 10px",
  border: "none",
  borderRadius: 8,
  background: "transparent",
  color: "var(--text-primary)",
  font: "inherit",
  fontSize: "var(--text-sm)",
  textAlign: "left",
  textDecoration: "none",
  cursor: "pointer",
};

const logoutStyle: CSSProperties = {
  ...itemStyle,
  color: "var(--brand-secondary)",
  fontWeight: 600,
};
